import React, { FC, useCallback, useEffect, useState } from 'react';
import { Button, Checkbox, Select, Space } from 'antd';
import styles from './Filters.module.less';
import { StepRangeSlider } from '../../../../components/StepRangeSlider/StepRangeSlider';
import { ContentBlock } from '../../../../uicomp/atmos/content-block/ContentBlock';
import { FilterTitle } from './FilterTitle';
import { useCachedData } from '../../../../connection/CacheContext';

export type SalarySelectorValue = {
  type: string;
  from?: number;
  to?: number;
  currency?: string;
  onlyCurrency?: boolean;
};
type Props = {
  value?: SalarySelectorValue;
  onChange?: (value?: SalarySelectorValue) => void;
  onCancel?: () => void;
};

export const SalarySelector: FC<Props> = (props) => {
  const { onChange } = props;
  const [value, setValue] = useState<SalarySelectorValue | undefined>(props.value);
  const [allCurrency, setAllCurrency] = useState<any[]>([]);
  const [stepData, setStepData] = useState<Record<string, any>>();
  const [steps, setSteps] = useState<any>();
  const [rightText, setRightText] = useState<string>();
  const [salarySteps, setSalarySteps] = useState<any>();
  const [currency, setCurrency] = useState<string>(value?.currency ?? '1');
  const [currencyName, setCurrencyName] = useState<string>();
  const [only, setOnly] = useState<boolean>(value?.onlyCurrency ?? false);
  const { mainType, masterdata } = useCachedData();

  const stepSeparator = (value: string) => {
    const stepsX: number[] = [];
    value.split(',').forEach((val) => {
      stepsX.push(parseInt(val));
    });
    return stepsX;
  };
  const changeRightText = useCallback(
    (stepsX: any) => {
      if (!value?.to) {
        return setRightText(`${stepsX[stepsX.length - 1].toLocaleString()}+`);
      }
      if (stepsX.indexOf(value.to) === stepsX.length - 1) {
        return setRightText(`${value.to.toLocaleString()}+`);
      }
      return setRightText(`${value.to.toLocaleString()}`);
    },
    [value?.to],
  );
  const stepChanger = useCallback(
    (id: string, stepDataX: any) => {
      const newSalaryStps: any = {
        ['Per Hour']: stepSeparator(stepDataX[id].hour),
        ['Per Day']: stepSeparator(stepDataX[id].day),
        ['Per Month']: stepSeparator(stepDataX[id].month),
        ['Per Year']: stepSeparator(stepDataX[id].year),
      };
      setSalarySteps(newSalaryStps);
      const temp = newSalaryStps[value?.type ?? 'Per Month'];
      setSteps(temp);
      setValue({
        type: value?.type ?? 'Per Month',
        from: temp[0],
        to: temp[temp.length - 1],
        currency: currency,
        onlyCurrency: only,
      });
    },
    [currency, only, value?.type],
  );
  useEffect(() => {
    const currencyId = mainType['Currency'];
    if (currencyId) {
      const options: any[] = [];
      const res = masterdata[currencyId];
      res.forEach((val) => {
        options.push({ label: val.value, value: val.id });
      });
      const name = res.filter((val) => val.id === currency)[0].value;
      setCurrencyName(name);
      setAllCurrency(options);
      const data = Object.fromEntries(
        res.map((val) => [
          val.id,
          {
            hour: val.perHour,
            day: val.perDay,
            month: val.perMonth,
            year: val.perYear,
          },
        ]),
      );
      setStepData(data);
      const newSalaryStps: any = {
        ['Per Hour']: stepSeparator(data[currency].hour),
        ['Per Day']: stepSeparator(data[currency].day),
        ['Per Month']: stepSeparator(data[currency].month),
        ['Per Year']: stepSeparator(data[currency].year),
      };
      setSalarySteps(newSalaryStps);
      const temp = newSalaryStps[value?.type ?? 'Per Month'];
      setSteps(temp);
      changeRightText(temp);
    }
  }, [changeRightText, currency, mainType, masterdata, value?.type]);
  const handleChange = useCallback(() => {
    const val = { ...value };
    val.currency = currency;
    val.onlyCurrency = only;
    if (val.from === steps[0]) {
      delete val.from;
    }
    if (val.to === steps[steps.length - 1]) {
      delete val.to;
    }
    if (val.from !== undefined || val.to !== undefined || only) {
      onChange?.(val as any);
    } else {
      onChange?.(undefined);
    }
  }, [currency, onChange, only, steps, value]);
  const onCurrencyChange = (val: string) => {
    const name = allCurrency.filter((x) => x.value === val)[0].label;
    setCurrencyName(name);
    setCurrency(val);
    stepChanger(val, stepData);
    changeRightText(steps);
  };

  return (
    <div className={styles.filterSalaryArea}>
      <Space direction="vertical" size={16} className={styles.filterSalaryArea}>
        <FilterTitle title={'Salary'} onApply={handleChange} onCancel={props.onCancel} />
        <Select
          style={{ width: '100%' }}
          value={value?.type ?? 'Per Month'}
          onChange={(type) => {
            const newSteps = salarySteps[type ?? 'Per Month'];
            setValue({
              type,
              from: newSteps[0],
              to: newSteps[newSteps.length - 1],
              currency: currency,
              onlyCurrency: only,
            });
          }}
          options={[
            { label: 'Per Hour', value: 'Per Hour' },
            { label: 'Per Day', value: 'Per Day' },
            { label: 'Per Month', value: 'Per Month' },
            { label: 'Per Year', value: 'Per Year' },
          ]}
        />
        <Space direction={'vertical'} size={16} className={styles.sliderContainer}>
          {steps && (
            <StepRangeSlider
              prefix={currencyName ?? 'LKR'}
              value={{ from: value?.from, to: value?.to }}
              onChange={(val) =>
                setValue({
                  type: value?.type ?? 'Per Month',
                  from: val?.from,
                  to: val?.to,
                  currency: currency,
                  onlyCurrency: only,
                })
              }
              steps={steps}
            />
          )}
          <ContentBlock
            style={{ fontSize: '0.9em' }}
            left={`${currencyName} ${(value?.from ?? 0).toLocaleString()}`}
            right={`${currencyName} ${rightText}`}>
            {' '}
          </ContentBlock>
        </Space>
        <Space size={15} className={styles.sliderContainer}>
          <Select
            style={{ width: '100%' }}
            value={currency}
            onChange={onCurrencyChange}
            options={allCurrency}
            filterOption={(input, option) =>
              option?.label?.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            showSearch
          />
          <Checkbox
            checked={only}
            onChange={(val) => setOnly(val.target.checked)}
            style={{
              fontSize: 14,
            }}>{`Only show jobs that pay the salary in ${currencyName}`}</Checkbox>
        </Space>
        <Button
          className={styles.applyButton}
          type={'primary'}
          size={'small'}
          onClick={handleChange}>
          Apply
        </Button>
      </Space>
    </div>
  );
};
